import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { Switch } from "@/components/Switch";
import { Typography } from "@/components/Typography";

const styles = {
  setting: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "12px",
  },
  settingText: {
    gap: "2px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface FooterFormProps {
  /**
   * The draft of the channel's `webChatFooterDisabled` choice: `true` hides
   * the "Powered by Crescendo Lab" footer.
   */
  footerDisabled: boolean;
  onFooterDisabledChange: (footerDisabled: boolean) => void;
  disabled: boolean;
  /**
   * Open on arrival when this is the only section on the page, so the switch
   * is not hidden behind a click.
   */
  defaultExpanded: boolean;
}

/**
 * The chat window footer, a paid capability. Lives outside the brand config
 * form: the choice is stored on the channel as `webChatFooterDisabled`, not
 * inside `webChatConfig`, so it is a plain controlled value here.
 */
export const FooterForm: React.FC<FooterFormProps> = function FooterForm({
  footerDisabled,
  onFooterDisabledChange,
  disabled,
  defaultExpanded,
}) {
  const { t } = useTranslation();
  const labelId = useId();

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary>
        <Typography variant="h3" fontWeight={500}>
          {t("webChatConfig.footer.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack sx={styles.setting}>
          <Switch
            checked={footerDisabled}
            disabled={disabled}
            onChange={(_, checked) => onFooterDisabledChange(checked)}
            inputProps={{ "aria-labelledby": labelId }}
          />
          <Stack sx={styles.settingText}>
            <Typography
              id={labelId}
              color={theme.colors.staticFgTitle}
              fontWeight={500}
            >
              {t("webChatConfig.footer.hideBranding.label")}
            </Typography>
            <Typography variant="note">
              {t("webChatConfig.footer.hideBranding.desc")}
            </Typography>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
